import { objectToCamel } from 'ts-case-convert';
import { LOCAL_STORAGE, LocalStorage } from '@blink/components/src/utils';

declare global {
    interface Window {
        configs: any;
    }
}

export class Api {
    static APP_DISPATCHER_ADDRESS =
        window?.configs?.REACT_APP_DISPATCHER_ADDRESS || process.env.REACT_APP_DISPATCHER_ADDRESS;
    static WC_PROJECT_ID =
        window?.configs?.REACT_APP_WC_PROJECT_ID || process.env.REACT_APP_WC_PROJECT_ID;
    static WC_HOME_URL =
        window?.configs?.REACT_APP_WC_HOME_URL || process.env.REACT_APP_WC_HOME_URL;
    static ARKIS_PORTAL_HTTP_URL =
        window?.configs?.REACT_APP_ARKIS_PORTAL_HTTP_URL ||
        process.env.REACT_APP_ARKIS_PORTAL_HTTP_URL;
    static ARKIS_PORTAL_WS_URL =
        window?.configs?.REACT_APP_ARKIS_PORTAL_WS_URL || process.env.REACT_APP_ARKIS_PORTAL_WS_URL;
    static REACT_APP_ANALYTICS_HTTP_URL =
        window?.configs?.REACT_APP_ANALYTICS_HTTP_URL || process.env.REACT_APP_ANALYTICS_HTTP_URL;
    static API_AUTH_ENDPOINT =
        window?.configs?.REACT_APP_API_AUTH_ENDPOINT || process.env.REACT_APP_API_AUTH_ENDPOINT;
    static OAUTH_IDP_LOGOUT_ENDPOINT =
        window?.configs?.REACT_APP_OAUTH_IDP_LOGOUT_ENDPOINT ||
        process.env.REACT_APP_OAUTH_IDP_LOGOUT_ENDPOINT;
    static WS_RPC_TIMEOUT_SECS =
        window?.configs?.REACT_APP_WS_RPC_TIMEOUT_SECS || process.env.REACT_APP_WS_RPC_TIMEOUT_SECS;
    static CLOSE_SIGNATURE_TTL_SECS =
        window?.configs?.REACT_APP_CLOSE_SIGNATURE_TTL_SECS ||
        process.env.REACT_APP_CLOSE_SIGNATURE_TTL_SECS;
    static WC_ETHEREUM_HTTP_URL =
        window?.configs?.REACT_APP_WC_ETHEREUM_HTTP_URL ||
        process.env.REACT_APP_WC_ETHEREUM_HTTP_URL;
    static API_AUTH_CLIENT_ID =
        window?.configs?.REACT_APP_API_AUTH_CLIENT_ID || process.env.REACT_APP_API_AUTH_CLIENT_ID;
    static LEND_BOOK_URL =
        window?.configs?.REACT_APP_LEND_BOOK_URL || process.env.REACT_APP_LEND_BOOK_URL;
    static BORROW_BOOK_URL =
        window?.configs?.REACT_APP_BORROW_BOOK_URL || process.env.REACT_APP_BORROW_BOOK_URL;

    static DATADOG_APPLICATION_ID =
        window?.configs?.REACT_APP_DATADOG_APPLICATION_ID ||
        process.env.REACT_APP_DATADOG_APPLICATION_ID;
    static DATADOG_CLIENT_TOKEN =
        window?.configs?.REACT_APP_DATADOG_CLIENT_TOKEN ||
        process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
    static DATADOG_SITE =
        window?.configs?.REACT_APP_DATADOG_SITE || process.env.REACT_APP_DATADOG_SITE;

    static DATADOG_SESSION_SAMPLE_RATE =
        window?.configs?.REACT_APP_DATADOG_SESSION_SAMPLE_RATE ||
        process.env.REACT_APP_DATADOG_SESSION_SAMPLE_RATE;

    static DATADOG_SESSION_REPLAY_SAMPLE_RATE =
        window?.configs?.REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE ||
        process.env.REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE;

    static DATADOG_PRIVACY_LEVEL =
        window?.configs?.REACT_APP_DATADOG_PRIVACY_LEVEL ||
        process.env.REACT_APP_DATADOG_PRIVACY_LEVEL;

    static hasRedirected = false;
    static cache: Map<string, any> = new Map();
    static cacheTTL: number = 5 * 60 * 1000; // 5 minutes
    static cacheTimestamps: Map<string, number> = new Map();

    static invalidateCache(url?: string) {
        if (url) {
            const urlWithApiKey = `${url}${url.includes('?') ? '&' : '?'}`;
            Api.cache.delete(urlWithApiKey);
            Api.cacheTimestamps.delete(urlWithApiKey);
        } else {
            Api.cache.clear();
            Api.cacheTimestamps.clear();
        }
    }

    private static pendingRequests: Map<string, Promise<any>> = new Map();

    private static buildCacheKey(method: string, url: string, body?: any): string {
        return `${method.toUpperCase()}:${url}:${body ? JSON.stringify(body) : ''}`;
    }

    static async getResponse<T>(
        url: string,
        init?: RequestInit,
        withoutCache?: boolean,
    ): Promise<T> {
        const hasInvalidateParam =
            url.includes('invalidateCache=true') ||
            (typeof window !== 'undefined' &&
                window.location.search.includes('invalidateCache=true'));

        const urlWithApiKey = `${url}${url.includes('?') ? '&' : '?'}`.replace(
            /invalidateCache=true&?|&invalidateCache=true/,
            '',
        );

        if (hasInvalidateParam) {
            Api.invalidateCache(urlWithApiKey);
        }

        const key = Api.buildCacheKey('GET', urlWithApiKey);

        if (!withoutCache && Api.cache.has(urlWithApiKey)) {
            const cachedAt = Api.cacheTimestamps.get(urlWithApiKey) || 0;
            const isFresh = Date.now() - cachedAt < Api.cacheTTL;

            if (isFresh) {
                return Api.cache.get(urlWithApiKey);
            } else {
                Api.cache.delete(urlWithApiKey);
                Api.cacheTimestamps.delete(urlWithApiKey);
            }
        }

        if (Api.pendingRequests.has(key)) {
            return Api.pendingRequests.get(key) as Promise<T>;
        }

        const requestPromise = retryWithDelay<T>(async () => {
            try {
                const data = await fetch(urlWithApiKey, {
                    headers: { ...init?.headers },
                    credentials: 'include',
                    signal: init?.signal,
                    ...init,
                });

                if (data.status === 401) {
                    return Api.handleError({ code: 401 });
                }

                if (data.ok) {
                    const json = await data.json();
                    const result = objectToCamel(json.result) as T;

                    if (!withoutCache) {
                        Api.cache.set(urlWithApiKey, result);
                        Api.cacheTimestamps.set(urlWithApiKey, Date.now());
                    }

                    return result;
                }

                const errorResponse = await data
                    .json()
                    .catch(() => ({ error: { message: 'Unknown error' } }));

                return Api.handleError(errorResponse);
            } catch (err) {
                return Api.handleError(err);
            } finally {
                Api.pendingRequests.delete(key);
            }
        });

        Api.pendingRequests.set(key, requestPromise);

        return requestPromise;
    }

    static async postResponse<T>(url: string, body: any, init?: RequestInit): Promise<T> {
        return retryWithDelay(async () => {
            const urlWithApiKey = `${url}${url.includes('?') ? '&' : '?'}`;
            const data = await fetch(urlWithApiKey, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...init?.headers,
                },
                body: JSON.stringify(body),
                credentials: 'include',
                ...init,
            });

            if (data.status === 401) {
                return Api.handleError({ code: 401 });
            }

            if (data.ok) {
                const responseBody = await data.text();
                return responseBody
                    ? (responseBody as unknown as T)
                    : ({ result: 'Success' } as unknown as T);
            }

            const errorResponse = await data
                .json()
                .catch(() => ({ error: { message: 'Unknown error' } }));
            return Promise.reject(errorResponse);
        });
    }

    static async putResponse<T>(url: string, body: any, init?: RequestInit): Promise<T> {
        return retryWithDelay(async () => {
            const urlWithApiKey = `${url}${url.includes('?') ? '&' : '?'}`;
            const data = await fetch(urlWithApiKey, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...init?.headers,
                },
                body: JSON.stringify(body),
                credentials: 'include',
                ...init,
            });

            if (data.status === 401) {
                return Api.handleError({ code: 401 });
            }

            if (data.ok) {
                const responseBody = await data.text();
                return responseBody
                    ? (responseBody as unknown as T)
                    : ({ result: 'Success' } as unknown as T);
            }

            return Promise.reject(data);
        });
    }

    static async patchResponse<T>(url: string, body: any, init?: RequestInit): Promise<T> {
        return retryWithDelay(async () => {
            const urlWithApiKey = `${url}${url.includes('?') ? '&' : '?'}`;

            const data = await fetch(urlWithApiKey, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...init?.headers,
                },
                body: JSON.stringify(body),
                credentials: 'include',
                ...init,
            });

            if (data.status === 401) {
                return Api.handleError({ code: 401 });
            }

            if (data.ok) {
                const responseBody = await data.text();

                if (!responseBody) {
                    return { result: 'Success' } as unknown as T;
                }

                return responseBody as unknown as T;
            }
            return Promise.reject(data);
        });
    }

    static login() {
        LocalStorage.setItem(LOCAL_STORAGE.LOGGED_IN, '');
        window.location.href = `${Api.API_AUTH_ENDPOINT}/start?rd=${window.location}`;
    }

    static handleError(e: any) {
        if (e?.code === 401 || e?.status === 401) {
            Api.login();
            return Promise.reject({ message: 'Unauthorized', error: e });
        }

        if (e?.message === 'Response JSON parsing failed') {
            console.error('Invalid JSON response:', e);
        }

        return Promise.reject(e);
    }
}

const retryWithDelay = async <T>(
    fn: () => Promise<T>,
    retries: number = 3,
    delayMs: number = 1000,
): Promise<T> => {
    let lastError: any;
    for (let attempt = 0; attempt < retries; attempt++) {
        try {
            return await fn();
        } catch (err) {
            lastError = err;
            if (attempt < retries - 1) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
        }
    }
    return Promise.reject(lastError);
};
